import { Box, Icon, Image, Theme, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { faCalendarAlt, fasStar } from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import {
  SaveHeart,
  decimalFormatter,
  useIsActivitySavedByCurrentUser
} from "@outschool/ui-components-website";
import { useDevicePixelRatio } from "@outschool/ui-utils";
import React from "react";

import { useActivityString } from "../../../../shared/hooks/useActivityString";

const IMAGE_SIZE = 72;

function Rating({ listing }: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\Listing"
  );
  const roundedRating =
    Math.round(listing.activity.averageStarRating * 2) / 2.0;

  const hasRatings = listing.activity.reviewCount > 0;

  return hasRatings ? (
    <>
      <Icon
        sx={{
          color: "#FDC840",
          paddingRight: "0.25em"
        }}
        icon={fasStar}
      />
      {/* eslint-disable i18next/no-literal-string */}
      <>
        {decimalFormatter(roundedRating, {
          minimumFractionDigits: 1
        })}{" "}
        ({listing.activity.reviewCount})
      </>
      {/* eslint-enable i18next/no-literal-string */}
    </>
  ) : (
    <span>{t("New class")}</span>
  );
}

export default function Listing({ listing, showSaved = false }: $TSFixMe) {
  const { translate } = useMachineTranslation();

  const { ageRangeString, durationString } = useActivityString();

  const isSaved = useIsActivitySavedByCurrentUser(listing.activity);
  const pixelRatio = useDevicePixelRatio();
  const photo = FilestackUrls.imageUrl(listing.activity.details.photo, {
    height: IMAGE_SIZE * pixelRatio,
    width: IMAGE_SIZE * pixelRatio,
    fit: "crop"
  });

  return (
    <>
      <Box
        sx={{
          color: "grey.900",
          display: "flex",
          alignItems: "flex-start"
        }}
      >
        <Box
          sx={{
            paddingRight: "12px",
            width: "100%"
          }}
        >
          <Box
            sx={{
              fontSize: "1.4rem",
              color: "grey.500",
              paddingBottom: "6px"
            }}
          >
            {/* eslint-disable i18next/no-literal-string */}
            <>
              <Rating listing={listing} /> ·{" "}
              {ageRangeString(listing.activity, true)}
            </>
            {/* eslint-enable i18next/no-literal-string */}
          </Box>

          <Typography
            variant="h4"
            sx={(theme: Theme) => ({
              fontSize: `${theme.typography.pxToRem(16)} !important`,
              lineHeight: "24px",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: "3",
              overflow: "hidden",
              fontFamily: "'Ginto Normal',sans-serif",
              wordWrap: "break-word",
              width: "24rem"
            })}
          >
            {translate(listing.activity.title)}{" "}
          </Typography>
          <Box
            sx={{
              fontSize: "1.4rem",
              color: "grey.500",
              paddingTop: "6px"
            }}
          >
            <Icon icon={faCalendarAlt} /> {durationString(listing.activity)}
          </Box>
        </Box>
        <Box
          sx={{
            position: "relative"
          }}
        >
          {" "}
          <Image
            sx={{
              width: "72px",
              height: "72px",
              borderRadius: "12px",
              objectFit: "cover"
            }}
            src={photo}
          />
          {showSaved && (
            <SaveHeart
              isSet={isSaved}
              activity={listing.activity}
              leader={listing.leader}
              unfilledColor="rgba(255,255,255,.8)"
              sx={{
                fontSize: "24px",
                position: "absolute",
                top: "6px",
                right: "6px"
              }}
              twoTone
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          paddingTop: "0.5em"
        }}
      >
        <Typography
          variant="inherit"
          sx={{
            color: "grey.500",
            fontSize: "1.6rem",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: "5",
            overflow: "hidden"
          }}
        >
          {listing.activity.summaryTranslated}
        </Typography>
      </Box>
    </>
  );
}
